import { useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  icon: string | null;
  parent_id: string | null;
  level: number;
  display_order: number;
  is_active: boolean;
  is_published: boolean;
  font_color: string | null;
  badge_label: string | null;
  badge_color: string | null;
  children?: Category[];
}

export interface CategoryWithPath extends Category {
  path: string[];
  pathNames: string[];
  fullPath: string;
}

// Build a tree structure from flat categories
function buildCategoryTree(categories: Category[]): Category[] {
  const categoryMap = new Map<string, Category>();
  const rootCategories: Category[] = [];

  categories.forEach((cat) => {
    categoryMap.set(cat.id, { ...cat, children: [] });
  });

  categories.forEach((cat) => {
    const category = categoryMap.get(cat.id)!;
    if (cat.parent_id && categoryMap.has(cat.parent_id)) {
      const parent = categoryMap.get(cat.parent_id)!;
      parent.children = parent.children || [];
      parent.children.push(category);
    } else if (!cat.parent_id) {
      rootCategories.push(category);
    }
  });

  const sortByOrder = (a: Category, b: Category) =>
    (a.display_order || 0) - (b.display_order || 0) || a.name.localeCompare(b.name);

  const sortTree = (cats: Category[]) => {
    cats.sort(sortByOrder);
    cats.forEach((cat) => {
      if (cat.children?.length) sortTree(cat.children);
    });
  };

  sortTree(rootCategories);
  return rootCategories;
}

// Build a tree filtering only active+published categories
function buildPublishedTree(categories: Category[]): Category[] {
  const published = categories.filter((c) => c.is_active && c.is_published);
  return buildCategoryTree(published);
}

// Flatten tree with path information
function flattenWithPath(
  categories: Category[],
  path: string[] = [],
  pathNames: string[] = []
): CategoryWithPath[] {
  const result: CategoryWithPath[] = [];

  categories.forEach((cat) => {
    const currentPath = [...path, cat.slug];
    const currentPathNames = [...pathNames, cat.name];

    result.push({
      ...cat,
      path: currentPath,
      pathNames: currentPathNames,
      fullPath: currentPathNames.join(" > "),
    });

    if (cat.children?.length) {
      result.push(...flattenWithPath(cat.children, currentPath, currentPathNames));
    }
  });

  return result;
}

export function useCategoryTree() {
  const query = useQuery({
    queryKey: ["category-tree"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .eq("is_active", true)
        .order("display_order")
        .order("name");

      if (error) throw error;

      const categories = (data || []).map((cat: any) => ({
        id: cat.id,
        name: cat.name,
        slug: cat.slug,
        description: cat.description,
        image_url: cat.image_url,
        icon: cat.icon,
        parent_id: cat.parent_id,
        level: cat.level ?? 0,
        display_order: cat.display_order ?? 0,
        is_active: cat.is_active ?? true,
        is_published: cat.is_published ?? true,
        font_color: cat.font_color ?? null,
        badge_label: cat.badge_label ?? null,
        badge_color: cat.badge_color ?? null,
      })) as Category[];

      return categories;
    },
    staleTime: 1000 * 60 * 5,
  });

  const tree = useMemo(() => {
    return query.data ? buildCategoryTree(query.data) : [];
  }, [query.data]);

  // Published tree: only active+published at every level
  const publishedTree = useMemo(() => {
    return query.data ? buildPublishedTree(query.data) : [];
  }, [query.data]);

  const flatList = useMemo(() => {
    return tree.length > 0 ? flattenWithPath(tree) : [];
  }, [tree]);

  const leafCategories = useMemo(() => {
    return flatList.filter((cat) => !cat.children?.length && cat.level >= 2);
  }, [flatList]);

  const getCategoryById = useCallback(
    (id: string): CategoryWithPath | undefined => {
      return flatList.find((cat) => cat.id === id);
    },
    [flatList]
  );

  const getAncestors = useCallback(
    (categoryId: string): CategoryWithPath[] => {
      const category = flatList.find((cat) => cat.id === categoryId);
      if (!category || category.path.length <= 1) return [];

      const ancestors: CategoryWithPath[] = [];
      category.path.slice(0, -1).forEach((slug) => {
        const ancestor = flatList.find((c) => c.slug === slug);
        if (ancestor) ancestors.push(ancestor);
      });

      return ancestors;
    },
    [flatList]
  );

  const searchCategories = useCallback(
    (searchQuery: string): CategoryWithPath[] => {
      if (!searchQuery.trim()) return flatList;
      const lowerQuery = searchQuery.toLowerCase();
      return flatList.filter(
        (cat) =>
          cat.name.toLowerCase().includes(lowerQuery) ||
          cat.fullPath.toLowerCase().includes(lowerQuery)
      );
    },
    [flatList]
  );

  return {
    categories: query.data || [],
    tree,
    publishedTree,
    flatList,
    leafCategories,
    isLoading: query.isLoading,
    error: query.error,
    getCategoryById,
    getAncestors,
    searchCategories,
    refetch: query.refetch,
  };
}

// Hook for category attributes
export function useCategoryAttributes(categoryId: string | null) {
  return useQuery({
    queryKey: ["category-attributes", categoryId],
    queryFn: async () => {
      if (!categoryId) return [];

      const { data: closureData } = await supabase
        .from("category_closure")
        .select("ancestor_id")
        .eq("descendant_id", categoryId);

      const ancestorIds = closureData?.map((c) => c.ancestor_id) || [categoryId];

      const { data, error } = await supabase
        .from("category_attributes")
        .select("*")
        .in("category_id", ancestorIds)
        .order("display_order");

      if (error) throw error;
      return data || [];
    },
    enabled: !!categoryId,
  });
}
